import { Box, Stack, Typography } from '@montugroup/design-system';
import type { Theme } from '@mui/material';
import { useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { StyledDescriptionList, StyledLink, StyledPrescriptionDetails } from './PrescriptionDetails.styles';
import PrescriptionDosageChip from './PrescriptionDosageChip';

import { PRODUCT_DETAIL_PATH } from '@/routes/route-paths';
import type { Prescription, ScriptMetadata } from './common';
import { getInterval, getQuantity, getStatus } from './common';

interface IPrescriptionDetails {
  prescription: Prescription;
  prescriptionMetaData: ScriptMetadata;
}

type DetailField = {
  label: string;
  getValue: (prescription: Prescription, prescriptionMetaData: ScriptMetadata, theme: Theme) => ReactNode;
  isShown?: (prescription: Prescription) => boolean;
};

const DETAIL_FIELDS: DetailField[] = [
  {
    label: 'Product Details',
    getValue: (prescription) => <StyledLink to={`${PRODUCT_DETAIL_PATH}${prescription.productId}`}>View</StyledLink>,
    isShown: (prescription) => !!prescription.productActive,
  },
  {
    label: 'Status',
    getValue: (_, meta) => {
      const status = getStatus(meta);
      const isActive = status === 'Active';

      return (
        <Typography sx={(theme) => ({ color: isActive ? theme.palette.success.main : theme.palette.error.main })}>
          {status}
        </Typography>
      );
    },
  },
  { label: 'Max quantity per repeat', getValue: getQuantity },
  { label: 'Interval time', getValue: getInterval },
  { label: 'Dosage', getValue: (prescription) => prescription.dosage ?? '-' },
];

export const PrescriptionDetails = ({ prescription, prescriptionMetaData }: IPrescriptionDetails) => {
  const theme = useTheme();

  const filteredDetails = DETAIL_FIELDS.filter((field) => (field.isShown ? field.isShown(prescription) : true));

  return (
    <StyledPrescriptionDetails>
      {(prescription.thcStrength || prescription.cbdStrength) && (
        <Stack direction="row" flexWrap="wrap" gap={theme.spacing(2)}>
          {prescription.thcStrength && <PrescriptionDosageChip dosage="THC" strength={prescription.thcStrength} />}
          {prescription.cbdStrength && <PrescriptionDosageChip dosage="CBD" strength={prescription.cbdStrength} />}
        </Stack>
      )}

      <StyledDescriptionList>
        {filteredDetails.map(({ label, getValue }) => {
          const fieldValue = getValue(prescription, prescriptionMetaData, theme);
          const isTextValue = typeof fieldValue === 'string' || typeof fieldValue === 'number';

          return (
            <Fragment key={label}>
              <Typography component="dt" fontWeight={theme.typography.fontWeightMedium}>
                {label}
              </Typography>

              {isTextValue ? (
                <Typography component="dd">{fieldValue}</Typography>
              ) : (
                <Box component="dd">{fieldValue}</Box>
              )}
            </Fragment>
          );
        })}
      </StyledDescriptionList>
    </StyledPrescriptionDetails>
  );
};
